import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconSparkles } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAuditLog,
  useI18nEntries,
  useDeepLSuggestion,
  useUpdateI18nEntry,
} from '@/api/useAdmin';
import type {
  FoerderprogrammForm,
  KonstanteForm,
  PauschaleLandForm,
  SachkontoForm,
  SachkontoGruppeForm,
} from '@/api/refTypes';
import {
  useBetreuungsmodellCreate,
  useBetreuungsmodellDelete,
  useBetreuungsmodellList,
  useBetreuungsmodellUpdate,
  useFoerderprogrammCreate,
  useFoerderprogrammDelete,
  useFoerderprogrammList,
  useFoerderprogrammUpdate,
  useKonstanteCreate,
  useKonstanteDelete,
  useKonstanteList,
  useKonstanteUpdate,
  usePauschaleLandCreate,
  usePauschaleLandDelete,
  usePauschaleLandList,
  usePauschaleLandUpdate,
  usePauschaleTraegerCreate,
  usePauschaleTraegerDelete,
  usePauschaleTraegerList,
  usePauschaleTraegerUpdate,
  useSachkontoCreate,
  useSachkontoDelete,
  useSachkontoGruppeCreate,
  useSachkontoGruppeDelete,
  useSachkontoGruppeList,
  useSachkontoGruppeUpdate,
  useSachkontoList,
  useSachkontoUpdate,
  useSueZulageCreate,
  useSueZulageDelete,
  useSueZulageList,
  useSueZulageUpdate,
  useSvSatzCreate,
  useSvSatzDelete,
  useSvSatzList,
  useSvSatzUpdate,
  useTvoedCreate,
  useTvoedDelete,
  useTvoedList,
  useTvoedUpdate,
  useTvoedVkaCreate,
  useTvoedVkaDelete,
  useTvoedVkaList,
  useTvoedVkaUpdate,
} from '@/api/useRefData';
import { useIsAdmin } from '@/auth/useRequireRole';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';

import { FieldConfig, RefDataTab } from './admin/RefDataTab';

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtEur(v: string | number, lang: string): string {
  const num = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'EUR',
  }).format(num);
}

function fmtPct(v: string | number): string {
  const num = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(num)) return '—';
  return `${(num * 100).toFixed(2)} %`;
}

const KATEGORIE_OPTIONS = [
  { value: 'u3', label: 'U3' },
  { value: 'ue3', label: 'Ü3' },
  { value: 'hort', label: 'Hort' },
  { value: 'ag', label: 'AG' },
  { value: 'ue6', label: 'Ü6' },
];

const STUNDENKLASSE_OPTIONS = [
  { value: 'bis_24', label: 'bis 24 h' },
  { value: '25_34', label: '25-34 h' },
  { value: '35_44', label: '35-44 h' },
  { value: 'ueber_45', label: '≥ 45 h' },
];

const PAUSCHALE_ART_OPTIONS = [
  { value: 'grund', label: 'Grundpauschale §32 (2)' },
  { value: 'qualitaet', label: 'Qualitätspauschale §32 (3)' },
  { value: 'schwerpunkt', label: 'Schwerpunkt-Kita §32 (4)' },
  { value: 'integration', label: 'Integration §32 (5)' },
  { value: 'kleinkita', label: 'KleinKita §32 (6)' },
  { value: 'leitung', label: 'Leitung §32 (2a) S.5' },
];

const STATUS_OPTIONS = [
  { value: 'aktiv', label: 'aktiv' },
  { value: 'pausiert', label: 'pausiert' },
  { value: 'ausgelaufen', label: 'ausgelaufen' },
];

const EINHEIT_OPTIONS = [
  { value: 'eur', label: 'EUR' },
  { value: 'prozent', label: 'Prozent' },
  { value: 'std', label: 'Stunden' },
  { value: 'faktor', label: 'Faktor (0–1)' },
  { value: 'kinder', label: 'Kinder (Anzahl)' },
];

export function AdminPage() {
  const { t, i18n } = useTranslation(['admin']);
  const lang = i18n.language;
  const isAdmin = useIsAdmin();
  const [stichtag, setStichtag] = useState<string | null>(todayISO());

  if (!isAdmin) {
    return (
      <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
        {t('admin:warning.adminOnly')}
      </Alert>
    );
  }

  return (
    <Stack>
      <Title order={2}>{t('admin:title')}</Title>
      <Tabs defaultValue="referenzdaten">
        <Tabs.List>
          <Tabs.Tab value="referenzdaten">{t('admin:tab.referenzdaten')}</Tabs.Tab>
          <Tabs.Tab value="uebersetzungen">{t('admin:tab.uebersetzungen')}</Tabs.Tab>
          <Tabs.Tab value="benutzer">{t('admin:tab.benutzer')}</Tabs.Tab>
          <Tabs.Tab value="audit">{t('admin:tab.audit')}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="referenzdaten" pt="md">
          <Tabs defaultValue="tvoed" orientation="vertical" variant="pills">
            <Tabs.List w={240} mr="md">
              <Tabs.Tab value="tvoed">TVöD-SuE</Tabs.Tab>
              <Tabs.Tab value="tvoed_vka">TVöD-VKA</Tabs.Tab>
              <Tabs.Tab value="sue">SuE-Zulage</Tabs.Tab>
              <Tabs.Tab value="sv">SV-Sätze</Tabs.Tab>
              <Tabs.Tab value="bm">Betreuungsmodelle</Tabs.Tab>
              <Tabs.Tab value="pl">§32 Land-Pauschalen</Tabs.Tab>
              <Tabs.Tab value="pt">Träger-Pauschalen</Tabs.Tab>
              <Tabs.Tab value="fp">Förderprogramme</Tabs.Tab>
              <Tabs.Tab value="k">Konstanten</Tabs.Tab>
              <Tabs.Tab value="skg">Sachkonto-Gruppen</Tabs.Tab>
              <Tabs.Tab value="sk">Sachkonten</Tabs.Tab>
            </Tabs.List>

            <Box style={{ flex: 1 }}>
              <Tabs.Panel value="tvoed">
                <TvoedSubTab stichtag={stichtag} onStichtag={setStichtag} lang={lang} />
              </Tabs.Panel>
              <Tabs.Panel value="tvoed_vka">
                <TvoedVkaSubTab stichtag={stichtag} onStichtag={setStichtag} lang={lang} />
              </Tabs.Panel>
              <Tabs.Panel value="sue">
                <SueZulageSubTab stichtag={stichtag} onStichtag={setStichtag} lang={lang} />
              </Tabs.Panel>
              <Tabs.Panel value="sv">
                <SvSatzSubTab stichtag={stichtag} onStichtag={setStichtag} />
              </Tabs.Panel>
              <Tabs.Panel value="bm">
                <BetreuungsmodellSubTab />
              </Tabs.Panel>
              <Tabs.Panel value="pl">
                <PauschaleLandSubTab stichtag={stichtag} onStichtag={setStichtag} lang={lang} />
              </Tabs.Panel>
              <Tabs.Panel value="pt">
                <PauschaleTraegerSubTab stichtag={stichtag} onStichtag={setStichtag} lang={lang} />
              </Tabs.Panel>
              <Tabs.Panel value="fp">
                <FoerderprogrammSubTab stichtag={stichtag} onStichtag={setStichtag} lang={lang} />
              </Tabs.Panel>
              <Tabs.Panel value="k">
                <KonstanteSubTab stichtag={stichtag} onStichtag={setStichtag} />
              </Tabs.Panel>
              <Tabs.Panel value="skg">
                <SachkontoGruppeSubTab stichtag={stichtag} onStichtag={setStichtag} />
              </Tabs.Panel>
              <Tabs.Panel value="sk">
                <SachkontoSubTab stichtag={stichtag} onStichtag={setStichtag} />
              </Tabs.Panel>
            </Box>
          </Tabs>
        </Tabs.Panel>

        <Tabs.Panel value="uebersetzungen" pt="md">
          <I18nTab />
        </Tabs.Panel>

        <Tabs.Panel value="benutzer" pt="md">
          <BenutzerTab />
        </Tabs.Panel>

        <Tabs.Panel value="audit" pt="md">
          <AuditTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Sub-Tabs
// ---------------------------------------------------------------------------

interface SubTabProps {
  stichtag: string | null;
  onStichtag: (d: string | null) => void;
  lang: string;
}

interface SubTabNoLangProps {
  stichtag: string | null;
  onStichtag: (d: string | null) => void;
}

function TvoedSubTab({ stichtag, onStichtag, lang }: SubTabProps) {
  const list = useTvoedList(stichtag);
  const create = useTvoedCreate();
  const update = useTvoedUpdate();
  const del = useTvoedDelete();

  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'eg_gruppe', label: 'Entgeltgruppe', kind: 'text', required: true, helperText: 'z.B. S 8a, S 13, I-SozPäd' },
    { name: 'stufe', label: 'Stufe', kind: 'text', required: true, helperText: '1–6' },
    { name: 'bruttowert_monatlich', label: 'Brutto monatlich (€)', kind: 'number', required: true },
    { name: 'jsz_anteil', label: 'JSZ-Anteil (0–1)', kind: 'percent', required: true, helperText: 'z.B. 0.85 = 85% Monatsbrutto' },
  ];

  return (
    <RefDataTab
      title="TVöD-Tabelle"
      description="Tarif-Brutto und Jahressonderzahlungs-Anteil pro Periode × EG × Stufe."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'gueltig_bis', label: 'Gültig bis' },
        { key: 'eg_gruppe', label: 'EG' },
        { key: 'stufe', label: 'Stufe' },
        {
          key: 'bruttowert_monatlich',
          label: 'Brutto/Monat',
          align: 'right',
          render: (r) => fmtEur(r.bruttowert_monatlich, lang),
        },
        {
          key: 'jsz_anteil',
          label: 'JSZ-Anteil',
          align: 'right',
          render: (r) => fmtPct(r.jsz_anteil),
        },
      ]}
      fields={fields}
      formInitial={() => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        eg_gruppe: '',
        stufe: '1',
        bruttowert_monatlich: '',
        jsz_anteil: '0.85',
      })}
      rowToForm={(r) => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        eg_gruppe: r.eg_gruppe,
        stufe: r.stufe,
        bruttowert_monatlich: r.bruttowert_monatlich,
        jsz_anteil: r.jsz_anteil,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function TvoedVkaSubTab({ stichtag, onStichtag, lang }: SubTabProps) {
  const list = useTvoedVkaList(stichtag);
  const create = useTvoedVkaCreate();
  const update = useTvoedVkaUpdate();
  const del = useTvoedVkaDelete();

  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'eg_gruppe', label: 'Entgeltgruppe', kind: 'text', required: true, helperText: 'z.B. EG 9a, EG 12' },
    { name: 'stufe', label: 'Stufe', kind: 'text', required: true, helperText: '1–6' },
    { name: 'bruttowert_monatlich', label: 'Brutto monatlich (€)', kind: 'number', required: true },
    { name: 'jsz_anteil', label: 'JSZ-Anteil (0–1)', kind: 'percent', required: true, helperText: 'z.B. 0.85 = 85% Monatsbrutto' },
  ];

  return (
    <RefDataTab
      title="TVöD-VKA-Tabelle"
      description="Tarif-Brutto und Jahressonderzahlungs-Anteil für sonstiges (nicht-pädagogisches) Personal."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'gueltig_bis', label: 'Gültig bis' },
        { key: 'eg_gruppe', label: 'EG' },
        { key: 'stufe', label: 'Stufe' },
        {
          key: 'bruttowert_monatlich',
          label: 'Brutto/Monat',
          align: 'right',
          render: (r) => fmtEur(r.bruttowert_monatlich, lang),
        },
        {
          key: 'jsz_anteil',
          label: 'JSZ-Anteil',
          align: 'right',
          render: (r) => fmtPct(r.jsz_anteil),
        },
      ]}
      fields={fields}
      formInitial={() => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        eg_gruppe: '',
        stufe: '1',
        bruttowert_monatlich: '',
        jsz_anteil: '0.85',
      })}
      rowToForm={(r) => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        eg_gruppe: r.eg_gruppe,
        stufe: r.stufe,
        bruttowert_monatlich: r.bruttowert_monatlich,
        jsz_anteil: r.jsz_anteil,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function SueZulageSubTab({ stichtag, onStichtag, lang }: SubTabProps) {
  const list = useSueZulageList(stichtag);
  const create = useSueZulageCreate();
  const update = useSueZulageUpdate();
  const del = useSueZulageDelete();
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'eg_gruppe', label: 'Entgeltgruppe', kind: 'text', required: true },
    { name: 'betrag_monatlich', label: 'Betrag monatlich (€)', kind: 'number', required: true, helperText: 'Default 130 € · S 11b/12/14–18 → 180 €' },
  ];
  return (
    <RefDataTab
      title="SuE-Zulage"
      description="Sozial- und Erziehungsdienst-Zulage je TVöD-EG."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'gueltig_bis', label: 'Gültig bis' },
        { key: 'eg_gruppe', label: 'EG' },
        {
          key: 'betrag_monatlich',
          label: 'Betrag/Monat',
          align: 'right',
          render: (r) => fmtEur(r.betrag_monatlich, lang),
        },
      ]}
      fields={fields}
      formInitial={() => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        eg_gruppe: '',
        betrag_monatlich: '130',
      })}
      rowToForm={(r) => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        eg_gruppe: r.eg_gruppe,
        betrag_monatlich: r.betrag_monatlich,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function SvSatzSubTab({ stichtag, onStichtag }: SubTabNoLangProps) {
  const list = useSvSatzList(stichtag);
  const create = useSvSatzCreate();
  const update = useSvSatzUpdate();
  const del = useSvSatzDelete();
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'art', label: 'Art', kind: 'select', required: true, options: [
      { value: 'gkv', label: 'GKV (Krankenversicherung)' },
      { value: 'grv', label: 'GRV (Rentenversicherung)' },
      { value: 'gav', label: 'GAV (Arbeitslosenversicherung)' },
      { value: 'gpv', label: 'GPV (Pflegeversicherung)' },
      { value: 'u1', label: 'U1 (Krankheits-Umlage)' },
      { value: 'u2', label: 'U2 (Mutterschutz-Umlage)' },
      { value: 'u3', label: 'U3 (Insolvenz-Umlage)' },
      { value: 'steuer', label: 'Steuer (Minijob)' },
      { value: 'insgu', label: 'INSGU (Minijob)' },
      { value: 'summe', label: 'Summe (Aggregat)' },
    ] },
    { name: 'satz_ag', label: 'AG-Anteil (0–1)', kind: 'percent', required: true },
    { name: 'satz_an', label: 'AN-Anteil (0–1)', kind: 'percent' },
    { name: 'minijob', label: 'Minijob-Satz', kind: 'bool' },
  ];
  return (
    <RefDataTab
      title="SV-Beitragssätze"
      description="Sozialversicherungs-Sätze für reguläre Beschäftigte und Minijobs."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'art', label: 'Art' },
        { key: 'minijob', label: 'Minijob', render: (r) => (r.minijob ? <Badge size="xs" color="orange">M</Badge> : '—') },
        { key: 'satz_ag', label: 'AG-Anteil', align: 'right', render: (r) => fmtPct(r.satz_ag) },
        { key: 'satz_an', label: 'AN-Anteil', align: 'right', render: (r) => fmtPct(r.satz_an) },
      ]}
      fields={fields}
      formInitial={() => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        art: 'gkv',
        satz_ag: '0',
        satz_an: '0',
        minijob: false,
      })}
      rowToForm={(r) => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        art: r.art,
        satz_ag: r.satz_ag,
        satz_an: r.satz_an,
        minijob: r.minijob,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function BetreuungsmodellSubTab() {
  const list = useBetreuungsmodellList();
  const create = useBetreuungsmodellCreate();
  const update = useBetreuungsmodellUpdate();
  const del = useBetreuungsmodellDelete();
  const fields: FieldConfig[] = [
    { name: 'schluessel', label: 'Schlüssel', kind: 'text', required: true, helperText: 'z.B. U3_25, UE3_45_GE' },
    { name: 'bezeichnung', label: 'Bezeichnung', kind: 'text', required: true },
    { name: 'altersgruppe', label: 'Altersgruppe', kind: 'select', required: true, options: [
      { value: 'U3', label: 'U3' },
      { value: 'UE3', label: 'Ü3' },
      { value: 'HORT', label: 'Hort' },
      { value: 'AG', label: 'AG' },
      { value: 'UE6', label: 'Ü6' },
    ] },
    { name: 'stunden_pro_woche', label: 'Wochenstunden', kind: 'number', required: true },
    { name: 'bmw', label: 'BMW (Mittelwert)', kind: 'number', required: true },
    { name: 'fkf', label: 'FKF (Fachkraftfaktor 0–1)', kind: 'percent', required: true, helperText: 'U3: 0.20 · Ü3: 0.07 · Hort: 0.06' },
    { name: 'max_gruppe', label: 'Max. Gruppengröße', kind: 'number', required: true },
    { name: 'aktiv', label: 'aktiv', kind: 'bool' },
  ];
  return (
    <RefDataTab
      title="Betreuungsmodell-Katalog"
      description="Wochenstunden + Fachkraftfaktor je Betreuungsmodell (Personalbedarf §25c HKJGB)."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      withStichtag={false}
      rowKey={(r) => r.id}
      columns={[
        { key: 'schluessel', label: 'Schlüssel' },
        { key: 'bezeichnung', label: 'Bezeichnung' },
        { key: 'altersgruppe', label: 'Alter' },
        { key: 'bmw', label: 'BMW', align: 'right' },
        { key: 'fkf', label: 'FKF', align: 'right' },
        { key: 'max_gruppe', label: 'max. Gruppe', align: 'right' },
        { key: 'aktiv', label: 'aktiv', render: (r) => (r.aktiv ? '✓' : '—') },
      ]}
      fields={fields}
      formInitial={() => ({
        schluessel: '',
        bezeichnung: '',
        altersgruppe: 'U3',
        stunden_pro_woche: '0',
        bmw: '0',
        fkf: '0',
        max_gruppe: 25,
        aktiv: true,
      })}
      rowToForm={(r) => ({
        schluessel: r.schluessel,
        bezeichnung: r.bezeichnung,
        altersgruppe: r.altersgruppe,
        stunden_pro_woche: r.stunden_pro_woche,
        bmw: r.bmw,
        fkf: r.fkf,
        max_gruppe: r.max_gruppe,
        aktiv: r.aktiv,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function PauschaleLandSubTab({ stichtag, onStichtag, lang }: SubTabProps) {
  const list = usePauschaleLandList(stichtag);
  const create = usePauschaleLandCreate();
  const update = usePauschaleLandUpdate();
  const del = usePauschaleLandDelete();
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'art', label: 'Pauschale-Art', kind: 'select', required: true, options: PAUSCHALE_ART_OPTIONS },
    { name: 'kategorie', label: 'Kategorie', kind: 'select_nullable', options: KATEGORIE_OPTIONS, helperText: 'Leer für nicht-altersgruppenspezifische Pauschalen (KleinKita, Leitung).' },
    { name: 'stundenklasse', label: 'Stundenklasse', kind: 'select_nullable', options: STUNDENKLASSE_OPTIONS, helperText: 'Nur bei Grund- und Integrationspauschale.' },
    { name: 'satz_jaehrlich', label: 'Satz / Jahr (€)', kind: 'number', required: true },
    { name: 'rechtsgrundlage', label: 'Rechtsgrundlage', kind: 'text', helperText: 'z.B. §32 (2) HKJGB' },
  ];
  return (
    <RefDataTab
      title="§32 HKJGB Land-Pauschalen"
      description="Grund-, Qualitäts-, Schwerpunkt-, Integrations-, KleinKita- und Leitungspauschale."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'art', label: 'Art' },
        { key: 'kategorie', label: 'Kategorie', render: (r) => r.kategorie ?? '—' },
        { key: 'stundenklasse', label: 'Stundenklasse', render: (r) => r.stundenklasse ?? '—' },
        { key: 'satz_jaehrlich', label: '€/Jahr', align: 'right', render: (r) => fmtEur(r.satz_jaehrlich, lang) },
        { key: 'rechtsgrundlage', label: '§', render: (r) => r.rechtsgrundlage ?? '—' },
      ]}
      fields={fields}
      formInitial={(): PauschaleLandForm => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        art: 'grund',
        kategorie: null,
        stundenklasse: null,
        satz_jaehrlich: '0',
        rechtsgrundlage: null,
      })}
      rowToForm={(r): PauschaleLandForm => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        art: r.art,
        kategorie: r.kategorie,
        stundenklasse: r.stundenklasse,
        satz_jaehrlich: r.satz_jaehrlich,
        rechtsgrundlage: r.rechtsgrundlage,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function PauschaleTraegerSubTab({ stichtag, onStichtag, lang }: SubTabProps) {
  const list = usePauschaleTraegerList(stichtag);
  const create = usePauschaleTraegerCreate();
  const update = usePauschaleTraegerUpdate();
  const del = usePauschaleTraegerDelete();
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'art', label: 'Pauschale-Art', kind: 'select', required: true, options: [
      { value: 'gutekita', label: 'Gute-Kita §32 (2a)' },
      { value: 'starke_teams', label: 'Starke Teams' },
      { value: 'kita_assistenz', label: 'Kita-Assistenz' },
    ] },
    { name: 'groessen_stufe', label: 'Größen-Stufe', kind: 'select', required: true, options: [
      { value: 'klein', label: 'klein' },
      { value: 'mittel', label: 'mittel' },
      { value: 'gross', label: 'groß' },
    ] },
    { name: 'von_kinder', label: 'von Kindern (≥)', kind: 'number' },
    { name: 'bis_kinder', label: 'bis Kindern (≤, leer = unbegrenzt)', kind: 'number' },
    { name: 'satz_jaehrlich', label: 'Satz / Jahr (€)', kind: 'number', required: true },
    { name: 'rechtsgrundlage', label: 'Rechtsgrundlage', kind: 'text' },
  ];
  return (
    <RefDataTab
      title="Trägergrößen-Pauschalen"
      description="Größengestaffelte Pauschalen (Gute-Kita, Starke Teams, Kita-Assistenz)."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'art', label: 'Art' },
        { key: 'groessen_stufe', label: 'Stufe' },
        { key: 'von_kinder', label: 'von', align: 'right', render: (r) => r.von_kinder ?? '—' },
        { key: 'bis_kinder', label: 'bis', align: 'right', render: (r) => r.bis_kinder ?? '∞' },
        { key: 'satz_jaehrlich', label: '€/Jahr', align: 'right', render: (r) => fmtEur(r.satz_jaehrlich, lang) },
      ]}
      fields={fields}
      formInitial={() => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        art: 'gutekita',
        groessen_stufe: 'klein',
        von_kinder: 0,
        bis_kinder: null,
        satz_jaehrlich: '0',
        rechtsgrundlage: null,
      })}
      rowToForm={(r) => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        art: r.art,
        groessen_stufe: r.groessen_stufe,
        von_kinder: r.von_kinder,
        bis_kinder: r.bis_kinder,
        satz_jaehrlich: r.satz_jaehrlich,
        rechtsgrundlage: r.rechtsgrundlage,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function FoerderprogrammSubTab({ stichtag, onStichtag, lang }: SubTabProps) {
  const list = useFoerderprogrammList(stichtag);
  const create = useFoerderprogrammCreate();
  const update = useFoerderprogrammUpdate();
  const del = useFoerderprogrammDelete();
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'schluessel', label: 'Schlüssel', kind: 'text', required: true },
    { name: 'bezeichnung', label: 'Bezeichnung', kind: 'text', required: true },
    { name: 'status', label: 'Status', kind: 'select', required: true, options: STATUS_OPTIONS },
    { name: 'betrag', label: 'Betrag (€)', kind: 'number' },
    { name: 'einheit', label: 'Einheit', kind: 'text', helperText: 'eur_pro_kind, eur_pro_monat, eur_pauschal' },
    { name: 'rechtsgrundlage', label: 'Rechtsgrundlage', kind: 'text' },
  ];
  return (
    <RefDataTab
      title="Land-Förderprogramme"
      description="Sprachförderung, Fachkräfteoffensive, Starke Teams, Kita-Assistenz etc."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'schluessel', label: 'Schlüssel' },
        { key: 'bezeichnung', label: 'Bezeichnung' },
        {
          key: 'status',
          label: 'Status',
          render: (r) => (
            <Badge
              size="sm"
              color={r.status === 'aktiv' ? 'green' : r.status === 'pausiert' ? 'yellow' : 'gray'}
              variant="light"
            >
              {r.status}
            </Badge>
          ),
        },
        { key: 'betrag', label: 'Betrag', align: 'right', render: (r) => (r.betrag ? fmtEur(r.betrag, lang) : '—') },
        { key: 'einheit', label: 'Einheit', render: (r) => r.einheit ?? '—' },
      ]}
      fields={fields}
      formInitial={(): FoerderprogrammForm => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        schluessel: '',
        bezeichnung: '',
        status: 'aktiv',
        betrag: null,
        einheit: null,
        rechtsgrundlage: null,
      })}
      rowToForm={(r): FoerderprogrammForm => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        schluessel: r.schluessel,
        bezeichnung: r.bezeichnung,
        status: r.status,
        betrag: r.betrag,
        einheit: r.einheit,
        rechtsgrundlage: r.rechtsgrundlage,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function KonstanteSubTab({ stichtag, onStichtag }: SubTabNoLangProps) {
  const list = useKonstanteList(stichtag);
  const create = useKonstanteCreate();
  const update = useKonstanteUpdate();
  const del = useKonstanteDelete();
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'schluessel', label: 'Schlüssel', kind: 'text', required: true, helperText: 'z.B. minijob_grenze, ausfallzeit_faktor' },
    { name: 'wert', label: 'Wert', kind: 'number', required: true },
    { name: 'einheit', label: 'Einheit', kind: 'select', required: true, options: EINHEIT_OPTIONS },
    { name: 'beschreibung', label: 'Beschreibung', kind: 'text' },
  ];
  return (
    <RefDataTab
      title="Konstanten"
      description="Skalare Werte (Minijob-Grenze, Ausfallzeit-Faktor, Schwellen)."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'schluessel', label: 'Schlüssel' },
        { key: 'wert', label: 'Wert', align: 'right' },
        { key: 'einheit', label: 'Einheit' },
        { key: 'beschreibung', label: 'Beschreibung' },
      ]}
      fields={fields}
      formInitial={(): KonstanteForm => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        schluessel: '',
        wert: '0',
        einheit: 'eur',
        beschreibung: null,
      })}
      rowToForm={(r): KonstanteForm => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        schluessel: r.schluessel,
        wert: r.wert,
        einheit: r.einheit,
        beschreibung: r.beschreibung,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function SachkontoGruppeSubTab({ stichtag, onStichtag }: SubTabNoLangProps) {
  const list = useSachkontoGruppeList(stichtag);
  const create = useSachkontoGruppeCreate();
  const update = useSachkontoGruppeUpdate();
  const del = useSachkontoGruppeDelete();
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'code', label: 'Code', kind: 'text', required: true, helperText: 'z.B. miete, verwaltung' },
    { name: 'bezeichnung', label: 'Bezeichnung', kind: 'text', required: true },
    { name: 'sort_order', label: 'Sortierung', kind: 'number', required: true },
  ];
  return (
    <RefDataTab
      title="Sachkonto-Gruppen"
      description="Oberste Ebene des Sachkosten-Kontenrahmens (z.B. „Miet- und Nebenkosten“). Steuert die Gruppierung in der Katalogansicht."
      rows={list.data}
      isLoading={list.isLoading}
      error={list.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'code', label: 'Code' },
        { key: 'bezeichnung', label: 'Bezeichnung' },
        { key: 'sort_order', label: 'Sortierung', align: 'right' },
      ]}
      fields={fields}
      formInitial={(): SachkontoGruppeForm => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        code: '',
        bezeichnung: '',
        sort_order: 0,
      })}
      rowToForm={(r): SachkontoGruppeForm => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        code: r.code,
        bezeichnung: r.bezeichnung,
        sort_order: r.sort_order,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

function SachkontoSubTab({ stichtag, onStichtag }: SubTabNoLangProps) {
  const gruppen = useSachkontoGruppeList(stichtag);
  const list = useSachkontoList(stichtag);
  const create = useSachkontoCreate();
  const update = useSachkontoUpdate();
  const del = useSachkontoDelete();
  const gruppenOptions = (gruppen.data ?? []).map((g) => ({
    value: g.code,
    label: `${g.bezeichnung} (${g.code})`,
  }));
  const fields: FieldConfig[] = [
    { name: 'gueltig_ab', label: 'Gültig ab', kind: 'date', required: true },
    { name: 'gueltig_bis', label: 'Gültig bis', kind: 'date_nullable' },
    { name: 'code', label: 'Code', kind: 'text', required: true, helperText: 'z.B. miete_pacht' },
    { name: 'gruppe_code', label: 'Gruppe', kind: 'select', required: true, options: gruppenOptions },
    { name: 'bezeichnung', label: 'Bezeichnung', kind: 'text', required: true },
    { name: 'sort_order', label: 'Sortierung', kind: 'number', required: true },
  ];
  return (
    <RefDataTab
      title="Sachkonten"
      description="Einzelne Konten des Kontenrahmens (z.B. „Miete, Pacht“). Jede Sachkosten-Position kann einem Konto zugeordnet werden — Ebene 1 der dreistufigen Bezeichnungs-Auflösung."
      rows={list.data}
      isLoading={list.isLoading || gruppen.isLoading}
      error={list.error ?? gruppen.error}
      stichtag={stichtag}
      onStichtagChange={onStichtag}
      rowKey={(r) => r.id}
      columns={[
        { key: 'gueltig_ab', label: 'Gültig ab' },
        { key: 'code', label: 'Code' },
        {
          key: 'gruppe_code',
          label: 'Gruppe',
          render: (r) => gruppenOptions.find((g) => g.value === r.gruppe_code)?.label ?? r.gruppe_code,
        },
        { key: 'bezeichnung', label: 'Bezeichnung' },
        { key: 'sort_order', label: 'Sortierung', align: 'right' },
      ]}
      fields={fields}
      formInitial={(): SachkontoForm => ({
        gueltig_ab: todayISO(),
        gueltig_bis: null,
        code: '',
        gruppe_code: gruppenOptions[0]?.value ?? '',
        bezeichnung: '',
        sort_order: 0,
      })}
      rowToForm={(r): SachkontoForm => ({
        gueltig_ab: r.gueltig_ab,
        gueltig_bis: r.gueltig_bis,
        code: r.code,
        gruppe_code: r.gruppe_code,
        bezeichnung: r.bezeichnung,
        sort_order: r.sort_order,
      })}
      onCreate={async (b) => {
        await create.mutateAsync(b);
      }}
      onUpdate={async (id, b) => {
        await update.mutateAsync({ id, body: b });
      }}
      onDelete={async (id) => {
        await del.mutateAsync(id);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Audit-Tab
// ---------------------------------------------------------------------------

function AuditTab() {
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const audit = useAuditLog({
    limit,
    offset,
    user: userFilter || undefined,
    action: actionFilter || undefined,
  });

  return (
    <Stack>
      <Group>
        <TextInput
          placeholder="User-Filter"
          value={userFilter}
          onChange={(e) => {
            setUserFilter(e.currentTarget.value);
            setOffset(0);
          }}
          w={240}
        />
        <TextInput
          placeholder="Action-Filter (z.B. ref.tvoed)"
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.currentTarget.value);
            setOffset(0);
          }}
          w={280}
        />
        {audit.data ? (
          <Badge variant="light">{audit.data.total} Einträge</Badge>
        ) : null}
      </Group>
      {audit.isLoading ? <LoadingState height={120} /> : null}
      {audit.error ? <ErrorState error={audit.error} /> : null}
      {audit.data && audit.data.items.length === 0 ? <EmptyState /> : null}
      {audit.data && audit.data.items.length > 0 ? (
        <Paper withBorder>
          <Table striped withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Zeit</Table.Th>
                <Table.Th>Benutzer</Table.Th>
                <Table.Th>Aktion</Table.Th>
                <Table.Th>Vorher</Table.Th>
                <Table.Th>Nachher</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {audit.data.items.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    {row.ts.replace('T', ' ').slice(0, 19)}
                  </Table.Td>
                  <Table.Td>{row.user_id}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light">
                      {row.action}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ maxWidth: 240, fontSize: 11 }}>
                    <code style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                      {row.before ? JSON.stringify(row.before).slice(0, 80) : '—'}
                    </code>
                  </Table.Td>
                  <Table.Td style={{ maxWidth: 240, fontSize: 11 }}>
                    <code style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                      {row.after ? JSON.stringify(row.after).slice(0, 80) : '—'}
                    </code>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      ) : null}
      {audit.data && audit.data.total > limit ? (
        <Group justify="space-between">
          <Button
            variant="default"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Zurück
          </Button>
          <Text size="sm" c="dimmed">
            {offset + 1}–{Math.min(offset + limit, audit.data.total)} von{' '}
            {audit.data.total}
          </Text>
          <Button
            variant="default"
            disabled={offset + limit >= audit.data.total}
            onClick={() => setOffset(offset + limit)}
          >
            Weiter
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Benutzer-Tab
// ---------------------------------------------------------------------------

function BenutzerTab() {
  const { t } = useTranslation(['admin']);
  // Aktive Nutzer aus Audit-Log ableiten — Keycloak-Admin-API-Integration ist
  // ein eigenes Vorhaben (Phase 4.2).
  const audit = useAuditLog({ limit: 500, offset: 0 });
  const byUser = useMemo(() => {
    const map = new Map<string, { count: number; lastTs: string }>();
    (audit.data?.items ?? []).forEach((row) => {
      const cur = map.get(row.user_id) ?? { count: 0, lastTs: row.ts };
      cur.count += 1;
      if (row.ts > cur.lastTs) cur.lastTs = row.ts;
      map.set(row.user_id, cur);
    });
    return Array.from(map.entries())
      .map(([user_id, info]) => ({ user_id, ...info }))
      .sort((a, b) => b.lastTs.localeCompare(a.lastTs));
  }, [audit.data]);

  return (
    <Stack>
      <Text c="dimmed" size="sm">
        {t('admin:users.subtitle')}. Quelle: Audit-Log der letzten 500 Schreibaktionen.
        Vollständige Keycloak-Reflektion ist Folge-Aufgabe (Admin-API-Integration).
      </Text>
      {audit.isLoading ? <LoadingState height={120} /> : null}
      {byUser.length > 0 ? (
        <Paper withBorder>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Benutzer</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Aktionen</Table.Th>
                <Table.Th>Letzte Aktivität</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {byUser.map((u) => (
                <Table.Tr key={u.user_id}>
                  <Table.Td>{u.user_id}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{u.count}</Table.Td>
                  <Table.Td>{u.lastTs.replace('T', ' ').slice(0, 19)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      ) : (
        <EmptyState />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// i18n-Editor (bisheriger Inhalt der "Übersetzungen"-Tabs)
// ---------------------------------------------------------------------------

function I18nTab() {
  const { t } = useTranslation(['admin']);
  const i18nEntries = useI18nEntries();
  const deepl = useDeepLSuggestion();
  const update = useUpdateI18nEntry();
  const [filter, setFilter] = useState('');
  const [edits, setEdits] = useState<Record<string, { de?: string; en?: string }>>(
    {},
  );

  const filteredI18n = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (i18nEntries.data ?? []).filter(
      (e) => !q || e.key.toLowerCase().includes(q) || e.namespace.toLowerCase().includes(q),
    );
  }, [i18nEntries.data, filter]);

  const onChange = (rowKey: string, lang: 'de' | 'en', value: string) => {
    setEdits((prev) => ({ ...prev, [rowKey]: { ...prev[rowKey], [lang]: value } }));
  };

  const onSave = (row: { namespace: string; key: string; values: Record<string, string> }) => {
    const rowKey = `${row.namespace}-${row.key}`;
    const draft = edits[rowKey] ?? {};
    update.mutate(
      {
        namespace: row.namespace,
        key: row.key,
        values: { ...row.values, ...draft },
      },
      {
        onSuccess: () => {
          setEdits((prev) => {
            const cp = { ...prev };
            delete cp[rowKey];
            return cp;
          });
        },
      },
    );
  };

  return (
    <Stack>
      <Group>
        <TextInput
          placeholder={t('admin:i18n.editor.search')}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          w={320}
        />
        <Badge variant="light">{filteredI18n.length}</Badge>
      </Group>
      {i18nEntries.isLoading ? <LoadingState /> : null}
      {i18nEntries.error ? <ErrorState error={i18nEntries.error} /> : null}
      {filteredI18n.length === 0 && i18nEntries.data ? <EmptyState /> : null}
      {filteredI18n.length > 0 ? (
        <Paper withBorder>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('admin:i18n.namespace')}</Table.Th>
                <Table.Th>{t('admin:i18n.key')}</Table.Th>
                <Table.Th>{t('admin:i18n.value.de')}</Table.Th>
                <Table.Th>{t('admin:i18n.value.en')}</Table.Th>
                <Table.Th aria-label="Aktionen" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredI18n.map((row) => {
                const rowKey = `${row.namespace}-${row.key}`;
                const draft = edits[rowKey] ?? {};
                const isDirty = draft.de !== undefined || draft.en !== undefined;
                return (
                  <Table.Tr key={rowKey}>
                    <Table.Td>{row.namespace}</Table.Td>
                    <Table.Td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.key}
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        value={draft.de ?? row.values.de ?? ''}
                        onChange={(e) => onChange(rowKey, 'de', e.currentTarget.value)}
                        size="xs"
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        value={draft.en ?? row.values.en ?? ''}
                        onChange={(e) => onChange(rowKey, 'en', e.currentTarget.value)}
                        size="xs"
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        {isDirty ? (
                          <Button size="xs" onClick={() => onSave(row)} loading={update.isPending}>
                            Speichern
                          </Button>
                        ) : null}
                        <Button
                          size="xs"
                          variant="subtle"
                          leftSection={<IconSparkles size={12} />}
                          loading={deepl.isPending}
                          onClick={() =>
                            deepl.mutate({
                              namespace: row.namespace,
                              key: row.key,
                              sourceLang: 'de',
                              targetLang: 'en',
                              sourceValue: row.values.de ?? '',
                            })
                          }
                        >
                          EN
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      ) : null}
    </Stack>
  );
}
